import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import FormatCurrency from 'components/Common/FormatCurrency'
import SolidExclamationCircleIcon from 'components/Luxkit/Icons/solid/SolidExclamationCircleIcon'
import AdditionalGuestsPopup from 'components/Booking/AdditionalGuestsPopup'
import Pill from 'components/Luxkit/Pill'

import { pluralizeToString } from 'lib/pluralize'
import { hasIncludedGuestsExceeded, getSumExtraGuestsSurcharge } from 'lib/cartReservationUtils'
import TaxesAndFees from 'components/OfferPage/PackageOption/Pricing/TaxesAndFees'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_HOTEL, OFFER_TYPE_LAST_MINUTE } from 'constants/offer'
import { formatOccupantsShort } from 'lib/occupancyUtils'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import getValueOffPercent from 'lib/getValueOffPercent'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import useHotelPriceColour from 'hooks/useHotelPriceColour'
import GeoContext from 'contexts/geoContext'
import SecureWithDepositTagAndTooltip from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndTooltip'
import { getDepositAmountPercentage, isDepositAvailableOnOffer, isDepositEnabledForOfferType } from 'lib/depositsUtils'
import noop from 'lib/noop'
import DepositTagOffersContext from 'contexts/depositTagOffersContext'
import getLPCRegionLOS from 'lib/getLPCRegionLOS'
import moment from 'moment'
import { buildSuggestedDatesParamsKey, buildSuggestedDatesString, getMinimumDurationFromFlexibleNights } from 'lib/searchUtils'
import { connect } from 'react-redux'
import { getSuggestedDates } from 'selectors/offerSelectors'
import { FLEXIBLE_DURATION_RANGE } from 'constants/search'
import ReserveForZeroTag from 'components/Common/ReserveForZeroTagAndTooltip/ReserveForZeroTag'
import { reserveForZeroEnabled } from 'checkout/selectors/featureConfig/reserveForZeroSelector'
import { isPackageEligibleForZeroReservation } from 'lib/reserveForZero'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import Group from 'components/Luxkit/Group'
import TextButton from 'components/Luxkit/Button/TextButton'
import PaletteProvider from 'contexts/PaletteProvider'
import CSSBreakpoint from 'components/Common/CSSBreakpoint'
import Spacing from 'components/Common/Spacing'
import { ValueWrapper } from 'components/Common/OfferPricing/styles'

export const StyledExclamationIcon = styled(SolidExclamationCircleIcon)`
  position: relative;
  margin-left: ${rem(5)};
  top: ${rem(3)};
`

interface Props {
  offer: App.Offer | App.OfferSummary;
  className?: string;
  rooms?: Array<App.Occupants>;
  pkg: App.Package;
  pricing?: App.OfferAvailableRate;
  hasDates?: boolean;
  checkInDate?: moment.Moment;
  flexibleMonths?: string;
  flexibleNights?: string;
}

interface MappedStateProps {
  suggestedDates: App.OfferSuggestedDates
  isReserveForZeroEnabled: boolean;
  defaultDepositAmountPercentage: number;
}

function HeroOfferTilePricing(props: Props & MappedStateProps) {
  const {
    offer,
    pkg,
    pricing,
    hasDates,
    rooms,
    checkInDate,
    suggestedDates,
    flexibleNights,
    flexibleMonths,
    isReserveForZeroEnabled,
    defaultDepositAmountPercentage,
  } = props
  const hasSuggestedCheckInCheckOut = suggestedDates?.checkIn && suggestedDates?.checkOut

  const { addDepositTagOffer = noop } = useContext(DepositTagOffersContext) ?? {}
  const { currentCurrency, showTaxExclusivePrices } = useContext(GeoContext)
  const priceMultiplier = pricing ? 1 : (rooms.length || 1)
  const extraGuestSurcharges = useMemo(() => getSumExtraGuestsSurcharge(pkg, rooms, offer), [pkg, rooms, offer])
  const taxesAndFees = (pricing?.taxesAndFees ?? pkg.taxesAndFees) || 0
  const propertyFees = (pricing?.propertyFees ?? pkg.propertyFees) * priceMultiplier || 0
  const exclusivePriceColor = useHotelPriceColour(taxesAndFees)

  let hotelPrice = (pricing ?? pkg).price
  hotelPrice = hotelPrice * priceMultiplier + extraGuestSurcharges
  const totalPrice = suggestedDates?.price ?? (showTaxExclusivePrices ? hotelPrice : hotelPrice + propertyFees)
  const totalValue = suggestedDates?.value ?? pkg.value * priceMultiplier + extraGuestSurcharges

  const showIncludedGuestsExceeded = useMemo(() => {
    if (!pkg.roomType || (pkg.roomRate?.extraGuestSurcharges?.length || 0) > 0) {
      return false
    }

    return rooms.some(room => hasIncludedGuestsExceeded(room, pkg, offer))
  }, [pkg, rooms, offer])

  const valueOffPercent = getValueOffPercent(totalValue, hotelPrice)
  const shouldShowRate = valueOffPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const shouldShowDiscountPercent = offer.type === OFFER_TYPE_LAST_MINUTE
  const shouldShowValue = pkg.value > 0 && totalValue > totalPrice && pkg.shouldDisplayValue &&
    !shouldShowDiscountPercent && (offer.type == OFFER_TYPE_HOTEL || !pkg.roomRate?.inclusionsHideValue)

  let staysFrom = offer.minDuration
  const LPCRegionLOS = getLPCRegionLOS(offer)
  if (LPCRegionLOS && offer.packages.some(p => p.duration === LPCRegionLOS)) {
    staysFrom = LPCRegionLOS
  }
  const showStaysFromLabel = !hasDates && offer.type === OFFER_TYPE_ALWAYS_ON && LPCRegionLOS && staysFrom < pkg.duration

  const showSecureWithDepositTag = isDepositEnabledForOfferType(offer.type) &&
    isDepositAvailableOnOffer(offer, currentCurrency, checkInDate, totalPrice, pkg, pricing)
  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)

  useEffect(() => {
    if (showSecureWithDepositTag) {
      addDepositTagOffer(offer.id)
    }
  }, [showSecureWithDepositTag, addDepositTagOffer, offer.id])

  const isReservableForZero = isReserveForZeroEnabled && isPackageEligibleForZeroReservation(offer, pkg.roomRate?.isReservableForZero, checkInDate)

  const duration = flexibleMonths && flexibleNights ? getMinimumDurationFromFlexibleNights(flexibleNights as FLEXIBLE_DURATION_RANGE) : undefined

  const getDurationText = useCallback(() => {
    if (hasDates) {
      return undefined
    }
    if (hasSuggestedCheckInCheckOut) {
      return buildSuggestedDatesString(suggestedDates.checkIn, suggestedDates.checkOut)
    }
    if (suggestedDates) {
      return buildSuggestedDatesString(suggestedDates?.checkIn, suggestedDates?.checkOut, duration)
    }
    if (offer.tileDurationLabel) {
      return `${offer.tileDurationLabel} from`
    }
    if (LPCRegionLOS) {
      return `${pkg.duration} nights from`
    }
    return 'From'
  }, [LPCRegionLOS, duration, hasDates, hasSuggestedCheckInCheckOut, offer.tileDurationLabel, pkg.duration, suggestedDates])

  return (
    <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="end">
      <Group direction="vertical">
        {showSecureWithDepositTag && !isReservableForZero && (
          <SecureWithDepositTagAndTooltip
            depositAmountPercentage={depositAmountPercentage}
            offerType={offer.type}
            depositThresholds={offer.depositThresholds}
            depositAmount={offer.depositAmount}
          />
        )}
        {isReservableForZero && (
          <PaletteProvider type="default">
            <Spacing mt={8}>
              <ReserveForZeroTag />
            </Spacing>
          </PaletteProvider>
        )}
        <Spacing mt={4}>
          <Caption variant="medium" colour="neutral-two">
            {hasDates && <>
              Total for {pluralizeToString('night', pkg.duration)}
              {rooms.length > 0 && `, ${formatOccupantsShort(rooms)}`}
              {' '}from
            </>}
            {getDurationText()}
          </Caption>
        </Spacing>
        <Heading data-testid="search-price" variant="heading3" colour={exclusivePriceColor}>
          <FormatCurrency value={totalPrice} format="roundedDollar" />
          {rooms.length === 0 && <Caption variant="medium" as="span" colour="neutral-two">/{offer.saleUnit}</Caption>}
        </Heading>
        {shouldShowValue && (
          <ValueWrapper variant="large" weight="bold" colour="neutral-one">
            Valued up to <FormatCurrency value={Math.ceil(totalValue)} />
            {shouldShowRate &&
              <PaletteProvider type="default">
                <Pill kind="primary" variant="success">-{valueOffPercent}%</Pill>
              </PaletteProvider>
            }
          </ValueWrapper>
        )}
        <TaxesAndFees
          property={offer.property}
          taxAmount={taxesAndFees}
        />
        {showStaysFromLabel &&
          <Caption colour="neutral-three" variant="medium">
            Stays from {pluralizeToString('night', staysFrom)} available
          </Caption>
        }
        {!!pkg.roomOccupancy && offer.type === 'tour' && (
          <Caption colour="neutral-two" variant="medium">
            {pkg.roomOccupancy}
          </Caption>
        )}
        {showIncludedGuestsExceeded && (
          <AdditionalGuestsPopup
            complex={false}
            message="+ Extra guest surcharge"
            description={pkg.roomPolicyDescription}
          />
        )}
      </Group>
      <CSSBreakpoint min="md">
        <TextButton kind="primary">
          View offer
        </TextButton>
      </CSSBreakpoint>
    </Group>
  )
}

HeroOfferTilePricing.defaultProps = {
  rooms: [],
} as Partial<Props>

export default connect<MappedStateProps, undefined, Props, App.State>((appState, ownProps) => {
  const flexibleSearchFilterKey = buildSuggestedDatesParamsKey(ownProps.flexibleMonths, ownProps.flexibleNights, ownProps.rooms)
  return {
    suggestedDates: getSuggestedDates(appState, flexibleSearchFilterKey, ownProps.offer.id),
    isReserveForZeroEnabled: reserveForZeroEnabled(appState),
    defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
  }
})(HeroOfferTilePricing)
